/**
 * Fungsi bantuan untuk kompresi gambar yang cerdas (Mempertahankan aspect ratio, kualitas, tapi size jauh lebih kecil).
 * Mengabaikan file non-gambar dan tidak akan mengubah jika file sudah cukup kecil.
 */
export const compressImage = (file: File, maxWidth = 1280, maxHeight = 1280, quality = 0.75, minSizeToCompress = 250 * 1024): Promise<File> => {
  return new Promise((resolve, reject) => {
    // 1. Lewati file non-gambar
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        const needsResize = width > maxWidth || height > maxHeight;

        // 2. Lewati kompresi jika gambar sudah kecil (dimensi & ukuran file)
        if (!needsResize && file.size < minSizeToCompress) {
          resolve(file);
          return;
        }

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(file); // Fallback jika canvas tidak didukung
        
        let outputType = file.type;
        let outputName = file.name;

        // Format PNG mengabaikan parameter "quality" (lossless).
        // Jika ukurannya besar, konversi ke JPEG agar bisa dikompres dengan agresif.
        if (file.type === 'image/png' && file.size > minSizeToCompress) {
          outputType = 'image/jpeg';
          outputName = outputName.replace(/\.png$/i, '.jpg');
          ctx.fillStyle = '#FFFFFF'; // Beri background putih agar area transparan tidak menjadi hitam
          ctx.fillRect(0, 0, width, height);
        }

        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }

          if (blob.size < file.size) {
            resolve(new File([blob], outputName, { type: outputType, lastModified: Date.now() }));
          } else if (needsResize) {
            // Canvas encoder kurang efisien dari native camera JPEG.
            // Coba lagi dengan quality lebih rendah supaya hasil resize tetap lebih kecil.
            const retryQuality = Math.min(quality * 0.6, 0.4);
            canvas.toBlob((blob2) => {
              if (blob2 && blob2.size < file.size) {
                resolve(new File([blob2], outputName, { type: outputType, lastModified: Date.now() }));
              } else {
                resolve(file); // Tetap kembalikan original jika masih lebih besar
              }
            }, outputType, retryQuality);
          } else {
            resolve(file);
          }
        }, outputType, quality);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};